import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Stack,
  IconButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
  API_ADD_UPDATE_FB_CATEGORY,
  API_DELETE_FB_CATEGORY_ID,
  API_GET_FB_CATEGORIES_DATA
} from '../../../Util/URLConstants';
import { formattedDateTime, renderSNo, toTitleCase } from '../../../Util/UtilHelper';
import { canMutateRecord } from '../../../Util/recordAuthorization';
import { SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import StorageFactory from '../../../Util/StorageFactory';
import { align_center, dataGridStyle } from '../../../Styles/VMStyles';
import Loader from '../../../Components/Loader';
import TitleWithAddButton from '../../Home/TitleWithAddButton';
import StyledInputField from '../../../Components/Custom/StyledInputField';
import ReusableDrawer from '../../../Components/ReusableDrawer';

function FeedbackCategory() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [feedbackId, setFeedbackId] = useState(null);
  const [categoryData, setCategoryData] = useState({ category_name: '' });
  const [errors, setErrors] = useState({ category_name: '' });

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_GET_FB_CATEGORIES_DATA}?user_id=${userID}`;
      const res = await fetch(url);
      const resp = await res.json();

      if (resp.status_code === 200 && Array.isArray(resp.data)) {
        const formattedData = resp.data.map((cls, idx) => ({
          s_no: idx + 1,
          id: cls.id || `${idx}`,
          category_name: cls.category_name || '',
          created_date: formattedDateTime(cls.created_date),
          created_by: cls.created_by,
        }));
        setRows(formattedData);
      } else {
        toast.error(resp.message || 'Unexpected response from server');
        setRows([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Failed to load categories');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userID]);

  const handleEditCategory = (rowData) => {
    setCategoryData({ category_name: rowData.category_name });
    setFeedbackId(rowData.id);
    setErrors({ category_name: '' });
    setDrawerOpen(true);
  };

  const handleDeleteCategory = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this category?");
    if (!confirm) return;

    try {
      const response = await axios.get(API_DELETE_FB_CATEGORY_ID, {
        params: {
          user_id: userID,
          id,
        },
      });

      const data = response.data;

      if (data.status_code === 200) {
        toast.success("Category removed successfully");
        fetchData();
      } else {
        toast.error(data.message || 'Failed to delete category');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error deleting category');
    }
  };

  const columns = [
       renderSNo(),
    { field: 'category_name', headerName: 'Category Name', flex: 1 },
    { field: 'created_date', headerName: 'Created Date', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.3,
      renderCell: (params) => (
        <Box sx={align_center}>
          {canMutateRecord(null, null, params.row) && (
            <IconButton onClick={() => handleEditCategory(params.row)} color="primary">
              <EditNoteIcon />
            </IconButton>
          )}
          {canMutateRecord(null, null, params.row) && (
            <IconButton onClick={() => handleDeleteCategory(params.row.id)} color="error">
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCategoryHandler = () => {
    setCategoryData({ category_name: '' });
    setFeedbackId(null);
    setErrors({ category_name: '' });
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleChange = (field) => (event) => {
    setCategoryData((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const newErrors = {
      category_name: categoryData.category_name.trim() === '' ? 'Category Name is required' : '',
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === '');
  };

  const submitHandler = async () => {
    const isValid = validateFields();
    if (!isValid) return;

    const finalPayload = {
      category_name: categoryData.category_name.trim(),
      user_id: userID,
      ...(feedbackId && { id: feedbackId }),
    };

    try {
      setLoading(true);
      const response = await axios.post(API_ADD_UPDATE_FB_CATEGORY, finalPayload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const respData = response.data;
      if (respData.status_code === 200) {
        toast.success('Category saved successfully');
        handleDrawerClose();
        fetchData();
      } else {
        toast.error(respData.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error:', error.message);
      toast.error('Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <TitleWithAddButton
        title="Feedback Categories"
        onAddClick={addCategoryHandler}
        buttonLabel="Create Category"
      />

      <DataGrid
        rows={rows}
        columns={columns}
        pagination
        autoHeight
        loading={loading}
        rowsPerPageOptions={[5, 10, 20, 50]}
        disableSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        hideFooterSelectedRowCount
        getRowHeight={() => 'auto'} 
        sx={dataGridStyle}
      />

      <ReusableDrawer
        open={drawerOpen}
        title="Create Category"
        onClose={handleDrawerClose}
        onSubmit={submitHandler}
        showFooter
        loading={loading}
      >
        <Box sx={{ p: 2 }}>
          <Stack spacing={2}>
            <StyledInputField
              heading="Category Name"
              placeholder={toTitleCase("Enter Category")}
              value={categoryData.category_name}
              onChange={handleChange('category_name')}
              fullWidth
              required
              error={Boolean(errors.category_name)}
              helperText={errors.category_name}
            />
          </Stack>
        </Box>
      </ReusableDrawer>
      <Loader loading={loading} />
    </Box>
  );
}

export default FeedbackCategory;
